import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const Hero: React.FC = () => {
    const { t } = useLanguage();
    const [counter, setCounter] = useState(0);
    const [floatingHearts, setFloatingHearts] = useState<{ id: number; x: number; size: number; emoji: string }[]>([]);

    const heartEmojis = ['💖', '💕', '💗', '💓', '💞', '💝', '💟', '💘', '💌', '🌹', '✨'];

    useEffect(() => {
        const timer = setTimeout(() => {
            const end = 50;
            const duration = 2000;
            const startTime = performance.now();

            function update(currentTime: number) {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeOutCubic = 1 - Math.pow(1 - progress, 3);
                setCounter(Math.floor(easeOutCubic * end));

                if (progress < 1) {
                    requestAnimationFrame(update);
                }
            }
            requestAnimationFrame(update);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const addHeart = () => {
        const newHeart = {
            id: Date.now(),
            x: Math.random() * 80 + 10,
            size: Math.random() * 20 + 20,
            emoji: heartEmojis[Math.floor(Math.random() * heartEmojis.length)],
        };
        setFloatingHearts(prev => [...prev, newHeart]);
        setTimeout(() => {
            setFloatingHearts(prev => prev.filter(h => h.id !== newHeart.id));
        }, 3000);
    };

    return (
        <div className="px-6 py-2 relative min-h-[350px] flex flex-col items-center">
            {/* Floating Hearts */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <AnimatePresence>
                    {floatingHearts.map(heart => (
                        <motion.div
                            key={heart.id}
                            initial={{ y: '100%', x: `${heart.x}%`, opacity: 1, scale: 0.5 }}
                            animate={{ y: '-10%', opacity: 0, scale: 1.5 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 3, ease: "easeOut" }}
                            className="absolute text-lg"
                            style={{ fontSize: heart.size }}
                        >
                            {heart.emoji}
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-6xl bg-white dark:bg-romantic-cardDark rounded-[2.5rem] p-4 md:p-8 shadow-2xl border border-primary-red/5 relative z-10"
            >
                <div className="flex flex-col items-center text-center">
                    {/* Circle Counter */}
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        onClick={addHeart}
                        className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white dark:bg-romantic-dark border-4 border-primary-red/10 flex flex-col items-center justify-center mb-4 cursor-pointer relative shadow-inner"
                    >
                        <span className="text-3xl md:text-4xl font-bold text-primary-red drop-shadow-md">
                            {counter}
                        </span>
                        <span className="text-[7px] font-bold text-gray-400 dark:text-gray-500 tracking-widest mt-0.5">DAYS OF LOVE</span>

                        {[0, 1, 2, 3, 4].map(i => (
                            <motion.div
                                key={i}
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 15 + i * 2, ease: "linear" }}
                                className="absolute w-full h-full flex items-start justify-center"
                                style={{ rotate: `${i * 72}deg` }}
                            >
                                <span className="text-sm mt-[-4px]">{heartEmojis[i]}</span>
                            </motion.div>
                        ))}
                    </motion.div>

                    <h2 className="text-xl md:text-2xl font-playfair font-bold text-primary-red mb-3">
                        {t('home.title')}
                    </h2>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="bg-primary-red/5 dark:bg-primary-red/10 p-4 md:p-6 rounded-[1.5rem] border border-primary-red/5 relative max-w-4xl w-full"
                    >
                        <p className="text-sm md:text-base font-poppins leading-relaxed text-gray-700 dark:text-gray-200">
                            {t('home.subtitle')}
                        </p>
                    </motion.div>

                    <div className="flex space-x-3 mt-4">
                        {heartEmojis.slice(0, 5).map((emoji, i) => (
                            <motion.button
                                key={i}
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={addHeart}
                                className="text-xl"
                            >
                                {emoji}
                            </motion.button>
                        ))}
                    </div>

                    <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-3">
                        Tap to send love
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default Hero;
